import React, { useEffect, useState } from "react";
import PageBody from "Common/Custom/PageBody";
import RoomDetailModal from "./RoomDetailModal";
import {
  CheckCircle,
  KeyRound,
  ClipboardList,
  LogOut,
  Sparkles,
  Wrench,
  AlertTriangle,
  Lock,
  Ban,
} from "lucide-react";

const STATUS_CONFIG = {
  "available":      { label: "Available",     gradient: "linear-gradient(135deg,#e8f5e9,#f1f8e9)", accent: "#2e7d32", Icon: CheckCircle },
  "checked-in":     { label: "Checked-In",    gradient: "linear-gradient(135deg,#fce4ec,#ffeeff)", accent: "#c62828", Icon: KeyRound },
  "booked":         { label: "Booked",        gradient: "linear-gradient(135deg,#fff3e0,#fff8e1)", accent: "#e65100", Icon: ClipboardList },
  "checked-out":    { label: "Checked-Out",   gradient: "linear-gradient(135deg,#f5f5f5,#eeeeee)", accent: "#616161", Icon: LogOut },
  "cleaning":       { label: "Cleaning",      gradient: "linear-gradient(135deg,#e0f7fa,#e8f5e9)", accent: "#00838f", Icon: Sparkles },
  "maintenance":    { label: "Maintenance",   gradient: "linear-gradient(135deg,#fffde7,#fff8e1)", accent: "#f57f17", Icon: Wrench },
  "dirty":          { label: "Dirty",         gradient: "linear-gradient(135deg,#f3e5f5,#ede7f6)", accent: "#6a1b9a", Icon: AlertTriangle },
  "blocked":        { label: "Blocked",       gradient: "linear-gradient(135deg,#eceff1,#e0e0e0)", accent: "#37474f", Icon: Lock },
  "do-not-reserve": { label: "Do Not Reserve",gradient: "linear-gradient(135deg,#fafafa,#f5f5f5)", accent: "#212121", Icon: Ban },
};

const getStatusConfig = (status) =>
  STATUS_CONFIG[(status || "").toLowerCase()] ||
  { label: status || "Available", gradient: "linear-gradient(135deg,#e8f5e9,#f1f8e9)", accent: "#2e7d32", Icon: CheckCircle };

const RoomStatusTable = ({ data }) => {
  const [now, setNow] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  const getRemainingTime = (checkoutDateStr) => {
    if (!checkoutDateStr) return null;
    const [datePart, timePart, meridian] = checkoutDateStr.split(" ");
    if (!datePart || !timePart || !meridian) return null;
    const [day, month, year] = datePart.split("/");
    const [hours, minutes, seconds] = timePart.split(":");
    let h = parseInt(hours, 10);
    if (meridian === "PM" && h !== 12) h += 12;
    if (meridian === "AM" && h === 12) h = 0;
    const checkoutDate = new Date(year, month - 1, day, h, minutes, seconds);
    const diffMs = checkoutDate - now;
    if (diffMs <= 0) return null;
    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const days = Math.floor(totalMinutes / (60 * 24));
    const hrs = Math.floor((totalMinutes % (60 * 24)) / 60);
    const mins = totalMinutes % 60;
    if (days > 0) return `${days}d ${hrs}h`;
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
  };

  const handleOpenModal = (room) => {
    setSelectedRoom(room);
    setShowModal(true);
  };

  const floors = data.reduce((acc, room) => {
    const floor = room.floor_name || "Unknown Floor";
    if (!acc[floor]) acc[floor] = [];
    acc[floor].push(room);
    return acc;
  }, {});

  return (
    <PageBody card={false}>
      {data.length === 0 && (
        <div className="text-center text-muted py-5">No rooms available</div>
      )}

      {Object.entries(floors).map(([floorName, rooms]) => (
        <div key={floorName} className="mb-5">

          {/* Floor heading */}
          <div className="d-flex align-items-center gap-3 mb-3">
            <div
              className="px-3 py-1 rounded-pill fw-semibold"
              style={{ background: "#212529", color: "#fff", fontSize: "0.8rem", letterSpacing: "0.05em" }}
            >
              {floorName}
            </div>
            <div className="flex-grow-1" style={{ height: 1, background: "linear-gradient(to right,#dee2e6,transparent)" }} />
            <span className="text-muted small">{rooms.length} rooms</span>
          </div>

          {/* Cards */}
          <div className="row g-3">
            {rooms.map((room) => {
              const { label, gradient, accent, Icon } = getStatusConfig(room.booking_status);
              const remaining = getRemainingTime(room.checkout_date);

              return (
                <div key={room.room_assign_id || room.room_no} className="col-xl-3 col-lg-4 col-md-6">
                  <div
                    onClick={() => handleOpenModal(room)}
                    style={{
                      background: gradient,
                      borderRadius: 16,
                      padding: "16px 18px",
                      cursor: "pointer",
                      position: "relative",
                      overflow: "hidden",
                      transition: "transform 0.18s, box-shadow 0.18s",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = "translateY(-3px)";
                      e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.12)";
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)";
                    }}
                  >
                    {/* Decorative circle */}
                    <div style={{
                      position: "absolute",
                      top: -20,
                      right: -20,
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      backgroundColor: accent,
                      opacity: 0.08,
                    }} />

                    {/* Top row: icon + status */}
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Icon size={18} color={accent} strokeWidth={2} />
                      <span
                        className="fw-semibold rounded-pill px-2 py-1"
                        style={{
                          fontSize: "0.65rem",
                          backgroundColor: accent,
                          color: "#fff",
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                        }}
                      >
                        {label}
                      </span>
                    </div>

                    {/* Room number */}
                    <div style={{ fontSize: "2rem", fontWeight: 800, color: "#1a1a2e", lineHeight: 1 }}>
                      {room.room_no}
                    </div>

                    {/* Room type */}
                    <div style={{ fontSize: "0.75rem", color: "#555", marginTop: 2, marginBottom: 10 }}>
                      {room.room_type || "Standard Room"}
                    </div>

                    {/* Divider */}
                    <div style={{ height: 1, backgroundColor: `${accent}30`, marginBottom: 10 }} />

                    {/* Guest */}
                    <div style={{
                      fontSize: "0.75rem",
                      color: "#444",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      marginBottom: 6,
                    }}>
                      {room.customer_name
                        ? <><span style={{ color: "#888" }}>👤 </span>{room.customer_name}</>
                        : <span style={{ color: "#aaa", fontStyle: "italic" }}>No guest assigned</span>
                      }
                    </div>

                    {/* Footer: dates + remaining */}
                    <div className="d-flex justify-content-between align-items-center">
                      <span style={{ fontSize: "0.68rem", color: "#888" }}>
                        {room.checkin_date
                          ? `${room.checkin_date.split(" ")[0]}${room.checkout_date ? " → " + room.checkout_date.split(" ")[0] : ""}`
                          : "—"
                        }
                      </span>
                      {remaining && (
                        <span style={{
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          color: accent,
                          backgroundColor: `${accent}15`,
                          borderRadius: 20,
                          padding: "2px 8px",
                        }}>
                          ⏱ {remaining}
                        </span>
                      )}
                      {!remaining && (
                        <span style={{ fontSize: "0.72rem", fontWeight: 600, color: accent }}>
                          {room.room_price || "—"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Legend */}
      <div className="d-flex flex-wrap gap-3 pt-3 border-top mt-2">
        {Object.values(STATUS_CONFIG).map(({ label, accent, Icon }) => (
          <div key={label} className="d-flex align-items-center gap-1">
            <Icon size={13} color={accent} strokeWidth={2} />
            <span className="small text-muted">{label}</span>
          </div>
        ))}
      </div>

      <RoomDetailModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        room={selectedRoom}
      />
    </PageBody>
  );
};

export default RoomStatusTable;
